import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem
} from '@mui/material';
import DateTimePicker from 'react-datetime-picker';
import {
  useCreateEditCustomBlogPost,
  useAdminGetCustomBlogPost
} from '@hooks/useCustomBlogPosts';
import { makeUrlSlugFromProductTitle } from '@utils/index';
import MarkdownEditor from '@components/MarkdownEditor';
import SpinningIcon from '@components/SpinningIcon';
import customBlogPostTypes from '@utils/constants/customBlogPostTypes';
import AddShortCut from '../AddShortCut';

interface CreateEditCustomBlogPostProps {
  onClose: () => void;
  id?: number | null;
}

const CreateEditCustomBlogPost = ({
  onClose,
  id
}: CreateEditCustomBlogPostProps) => {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState('NO_TYPE');
  const [description, setDescription] = useState('');
  const [seoImage, setSeoImage] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  const [isLive, setIsLive] = useState(true);
  const [postText, setPostText] = useState('');
  const [publishedAt, setPublishedAt] = useState(new Date());
  const [expiresAt, setExpiresAt] = useState(null);
  const [commaSepASINsMentioned, setCommaSepASINsMentioned] = useState('');
  const { mutate: createEditCustomBlogPost, isLoading: isSaving } =
    useCreateEditCustomBlogPost();
  const { data: customBlogPost } = useAdminGetCustomBlogPost(id);

  useEffect(() => {
    if (customBlogPost) {
      setTitle(customBlogPost.title);
      setSlug(customBlogPost.slug);
      setIsPinned(customBlogPost.isPinned);
      setIsLive(customBlogPost.isLive);
      setPostText(customBlogPost.postText);
      setPublishedAt(new Date(customBlogPost.publishedAt));
      setDescription(customBlogPost.description);
      setSeoImage(customBlogPost.seoImage);
      setType(customBlogPost.type);
      setExpiresAt(customBlogPost.expiresAt);
      setCommaSepASINsMentioned(customBlogPost.commaSepASINsMentioned);
    }
  }, [customBlogPost]);

  const resetFields = () => {
    setTitle('');
    setSlug('');
    setIsPinned(false);
    setIsLive(true);
    setPostText('');
    setPublishedAt(new Date());
    setDescription('');
    setSeoImage('');
    setType('NO_TYPE');
    setExpiresAt(null);
    setCommaSepASINsMentioned('');
  };

  const onClosePress = () => {
    resetFields();
    onClose();
  };

  const saveBlogPost = () => {
    createEditCustomBlogPost(
      {
        id: id || null,
        title,
        slug,
        isPinned,
        postText,
        isLive,
        authorId: 1,
        publishedAt,
        description,
        commaSepASINsMentioned,
        seoImage,
        type,
        expiresAt
      },
      {
        onSuccess: () => {
          onClosePress();
        }
      }
    );
  };

  const isCustomBlogPostValid =
    !title || !slug || !postText || !description || !seoImage;

  const ASINsMentioned = (commaSepASINsMentioned || '')
    .split(',')
    .map((ASIN) => ASIN.trim())
    .filter((ASIN) => ASIN);

  return (
    <>
      <Dialog
        open
        onClose={onClosePress}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        // open to widest
        maxWidth="xl"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '100%',
            margin: 0
          }
        }}
      >
        <DialogTitle id="alert-dialog-title">
          {id ? 'Edit ' : 'Create '} Custom Blog Post
        </DialogTitle>
        <DialogContent>
          <Paper
            sx={{
              padding: 4,
              margin: '8px auto',
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            <Box mb={3}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                variant="outlined"
                onBlur={() => {
                  if (!slug) {
                    setSlug(makeUrlSlugFromProductTitle(title));
                  }
                }}
              />
            </Box>
            <Box mb={3}>
              <TextField
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>
            <Box mb={3}>
              <TextField
                label="SEO Image"
                value={seoImage}
                onChange={(e) => setSeoImage(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>
            <Box mb={3}>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>
            <Box mb={3}>
              <TextField
                label="Comma Separated ASINs Mentioned"
                value={commaSepASINsMentioned}
                onChange={(e) => setCommaSepASINsMentioned(e.target.value)}
                fullWidth
                variant="outlined"
              />
            </Box>
            <Box mb={3}>
              <DateTimePicker
                onChange={setPublishedAt}
                value={publishedAt}
                clearIcon={null}
              />
              <Button
                variant="outlined"
                onClick={() => setPublishedAt(new Date())}
                size="small"
                sx={{
                  marginLeft: '4px'
                }}
              >
                Reset to now
              </Button>
            </Box>
            <Box>
              <Typography variant="h6" mb={2}>
                Shortcuts
              </Typography>
              {ASINsMentioned.map((ASIN) => (
                <AddShortCut
                  key={ASIN}
                  setPostText={setPostText}
                  postText={postText}
                  ASIN={ASIN}
                />
              ))}
            </Box>
            <Typography variant="h6" mb={2}>
              Post Content
            </Typography>
            <MarkdownEditor postText={postText} setPostText={setPostText} />
            <Box mt={3} mb={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPinned}
                    onChange={() => setIsPinned(!isPinned)}
                  />
                }
                label="Pin to top"
              />
            </Box>
            <Box mt={3} mb={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isLive}
                    onChange={() => setIsLive(!isLive)}
                  />
                }
                label="Is live"
              />
            </Box>
            <Box mt={3} mb={3}>
              <Select
                value={type}
                onChange={(e) => setType(e.target.value)}
                fullWidth
                variant="outlined"
              >
                {customBlogPostTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box mb={3}>
              <DateTimePicker onChange={setExpiresAt} value={expiresAt} />
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button variant="outlined" onClick={onClosePress}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={saveBlogPost}
              disabled={isCustomBlogPostValid || isSaving}
              startIcon={isSaving ? <SpinningIcon isLoading /> : null}
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateEditCustomBlogPost;
